import { cn } from '@neo/lib/utils'
import { ScrollArea } from '@neo/primitives/scroll-area'
import {
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@neo/primitives/sheet'

interface SideSheetProps {
  children: React.ReactNode
  title: React.ReactNode
  description?: string
  footer?: React.ReactNode
  className?: string
  size?: 'sm' | 'md' | 'lg'
  detached?: boolean
  onInteractOutside?: (e: Event) => void
  /** Hide the top-right close icon, e.g. when the footer already has Cancel. */
  showClose?: boolean
  /**
   * Radix focuses the first tabbable element when the sheet opens, which rings
   * the first input. Pass a handler to redirect that — see ComplianceSheet.
   */
  onOpenAutoFocus?: (e: Event) => void
}

const sizeClasses = {
  sm: 'sm:max-w-[424px] w-[424px]',
  md: 'sm:max-w-[550px] w-[550px]',
  lg: 'sm:max-w-[975px] w-[975px]',
}

export const SideSheet: React.FC<SideSheetProps> = ({
  children,
  title,
  description,
  footer,
  className,
  size = 'md',
  detached = true,
  onInteractOutside,
  showClose = true,
  onOpenAutoFocus,
}) => (
  <SheetContent
    detached={detached}
    className={cn('flex flex-col pr-0', sizeClasses[size], className)}
    onInteractOutside={onInteractOutside}
    showClose={showClose}
    onOpenAutoFocus={onOpenAutoFocus}
  >
    <SheetHeader>
      <SheetTitle>{title}</SheetTitle>
      {description && <SheetDescription>{description}</SheetDescription>}
    </SheetHeader>
    <ScrollArea type="scroll">
      <div className="px-0.5 pr-6">{children}</div>
    </ScrollArea>
    <SheetFooter className="mt-auto gap-2 sm:justify-between pr-6">{footer}</SheetFooter>
  </SheetContent>
)
